import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

// Renter beware notice, shown ONCE per match the first time a RENTER opens
// the conversation. Listers never see it and are never blocked by it.
// WHY localStorage per match: the dismissal is a device level "I read this"
// flag, not account data, so a simple key keeps it out of the API entirely.
const storageKey = (matchId) => `swiprent_renter_notice_${matchId}`;

function alreadyDismissed(matchId) {
  try {
    return window.localStorage.getItem(storageKey(matchId)) === "1";
  } catch {
    // Storage can be unavailable (private mode); showing the notice again
    // is the safe failure, silently skipping it is not.
    return false;
  }
}

export default function RenterNoticeModal({ matchId, mode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (mode !== "renter" || !matchId) return;
    if (!alreadyDismissed(matchId)) setOpen(true);
  }, [matchId, mode]);

  function dismiss() {
    try {
      window.localStorage.setItem(storageKey(matchId), "1");
    } catch {
      // Best effort: worst case the notice shows again next visit.
    }
    setOpen(false);
  }

  if (mode !== "renter") return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            role="dialog"
            aria-modal="true"
            aria-label="Before you go further"
            className="w-full rounded-t-3xl bg-night-surface p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-w-md sm:rounded-3xl sm:pb-6"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-night-tint text-brand-primary">
              <ShieldCheck size={22} strokeWidth={2} />
            </span>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-night-text">
              Before you go further
            </h2>
            <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-night-sub">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-primary" />
                Meet at the unit and see it in person before paying anything or signing anything.
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-primary" />
                SwipRent verifies identities and reviews listings, but we cannot guarantee a listing.
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-primary" />
                The responsibility to verify the place and the person stays with you.
              </li>
            </ul>
            <button
              type="button"
              onClick={dismiss}
              className="mt-6 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
            >
              I understand
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
